import { usePost } from "@albertine/api-client";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import type {
    CatalogItemFull,
    SendChatMessageToMember,
    SendChatMessageToMemberAttachment,
} from "@albertine/types/openapi";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import type { Agent } from "../../common/types/Agent";
import type { AttachmentWithFile } from "../../common/types/Attachment";
import type { BookingInMessage } from "../../common/types/Booking";
import type { Conversation } from "../../common/types/Conversation";
import type {
    AgentNewMessagePayload,
    AgentWriteInternalNotePayload,
} from "../../common/types/Message";
import type { Request } from "../../common/types/Request";
import type { CatalogArticleItem } from "../../common/types/Storyblok";
import { EventEmitter, type EventListener } from "../../EventEmitter";
import { writeInternalNote } from "../../FirebaseBackendActions";
import routes from "../../utils/routes";
import { firebaseTimestampFromDate } from "../../utils/timestamp.util";
import {
    createChatContextState,
    createNewMessage,
    type MessageInputContent,
    type MessageType,
} from "./ChatContext";
import MessageInput from "./MessageInput";
import { useUploadChatAttachmentFiles } from "./useUploadChatAttachmentFiles";

type Props = {
    request: Request | undefined;
    conversation: Conversation;
    currentAgent: Agent;
    member: MemberDataFirestoreRecord;
};

function ChatReply(props: Props) {
    const { conversation, currentAgent, member, request } = props;

    const chatContextState = createChatContextState(
        useState<string | undefined>(),
        useState<MessageType>("message"),
        useState<string | undefined>(),
        useState<string | undefined>(),
        useState<BookingInMessage[]>([]),
        useState<AttachmentWithFile[]>([]),
        useState<CatalogArticleItem[]>([]),
        useState<CatalogItemFull[]>([]),
    );

    const history = useHistory();

    useEffect(() => {
        // Still a bit sketchy pattern to allow sideways communication between chatContext and other components
        // ChatContext.Provider was a really slow implementation causing a lot of re-renders

        const addFilesToChatAttachments: EventListener = {
            name: "addFilesToChatAttachments",
            listener: (params) => {
                chatContextState.addFilesToAttachments(params.files);
            },
        };
        const changeChatContextKey: EventListener = {
            name: "changeChatContextKey",
            listener: (params) => {
                chatContextState.changeContextKey(params.id);
            },
        };
        const addBookingToChatAttachments: EventListener = {
            name: "addBookingToChatAttachments",
            listener: (params) => {
                chatContextState.addBooking(params.booking);
            },
        };
        const addCatalogItemToChatAttachments: EventListener = {
            name: "addCatalogItemToChatAttachments",
            listener: (params) => {
                chatContextState.addArticleV2(params.item);
            },
        };

        EventEmitter.addListener(addFilesToChatAttachments);
        EventEmitter.addListener(changeChatContextKey);
        EventEmitter.addListener(addBookingToChatAttachments);
        EventEmitter.addListener(addCatalogItemToChatAttachments);

        return () => {
            EventEmitter.removeListener(addFilesToChatAttachments);
            EventEmitter.removeListener(changeChatContextKey);
            EventEmitter.removeListener(addBookingToChatAttachments);
            EventEmitter.removeListener(addCatalogItemToChatAttachments);
        };
    }, [chatContextState]);

    const { mutateAsync: sendGeneralChatMessage } = usePost(
        "agentSendGeneralChatMessage",
    );
    const { mutateAsync: sendRequestChatMessage } = usePost(
        "agentSendRequestChatMessage",
    );

    const { uploadAttachmentFiles } = useUploadChatAttachmentFiles();

    const sendMessage = async (content: MessageInputContent) => {
        switch (content.messageType) {
            case "message": {
                const attachmentFiles = (content.attachments ?? [])
                    .map((it) => it.file)
                    .filter((it) => it !== undefined);
                const attachmentIds = await uploadAttachmentFiles(
                    attachmentFiles,
                    member.id,
                );
                return request
                    ? sendRequestChatMessage({
                          param: { memberId: member.id, requestId: request.id },
                          json: buildSendMessagePayload(content, attachmentIds),
                      })
                    : sendGeneralChatMessage({
                          param: { memberId: member.id },
                          json: buildSendMessagePayload(content, attachmentIds),
                      });
            }
            case "internal_note": {
                const newMessage: AgentNewMessagePayload = await createNewMessage(
                    content,
                    request,
                    currentAgent,
                    conversation,
                    member,
                );
                const payload: AgentWriteInternalNotePayload = {
                    conversation: conversation.id,
                    note: newMessage,
                    mentions: content.mentionedAgents.map((it) => it.id),
                };
                if (content.dueDate)
                    payload.mentionDueDate = firebaseTimestampFromDate(content.dueDate);
                return writeInternalNote(payload);
            }
            default:
                return Promise.reject();
        }
    };

    const attachArticles = () => {
        history.push(
            routes.catalogSearchInit(
                "proactivity",
                { discoverableForTenant: member.tenant },
                undefined,
                undefined,
                request?.id,
                request ? undefined : member.id,
            ),
        );
    };

    return (
        <MessageInput
            action="send"
            request={request}
            currentAgent={currentAgent}
            member={member}
            messageState={chatContextState}
            sendAction={sendMessage}
            attachAction={request ? undefined : attachArticles}
            conversation={conversation}
        />
    );
}

function buildSendMessagePayload(
    input: MessageInputContent,
    fileUploadIds: string[],
): SendChatMessageToMember {
    const attachments: SendChatMessageToMemberAttachment[] = [];
    for (const article of input.articlesV2 ?? []) {
        attachments.push({
            type: "catalog-item",
            id: article.id,
        });
    }
    for (const booking of input.bookings ?? []) {
        attachments.push({
            type: "booking-suggestion",
            bookingId: booking.id,
        });
    }
    for (const fileUploadId of fileUploadIds) {
        attachments.push({
            type: "file",
            fileUploadId: fileUploadId,
        });
    }
    return {
        idempotencyKey: crypto.randomUUID(),
        attachments,
        content: {
            type: "text",
            message: input.replyText ?? "",
            messageHtml: input.replyHtml ?? null,
        },
    };
}

export default ChatReply;
